import React, { useState, useMemo } from "react";
import {
  Plus,
  Trash2,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Bookmark,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { useWatchlistStore } from "../store/watchlistStore";
import type { WatchlistItem } from "../store/watchlistStore";
import AddToWatchlistModal from "./AddToWatchlistModal";
import AIAnalysisModal from "./AIAnalysisModal";
import Sparkline from "./Sparkline";
import TradingViewModal from "./TradingViewModal";
import { buttonClass } from './ui/Button';

const StockLogo: React.FC<{ item: WatchlistItem; size?: "sm" | "md" }> = ({
  item,
  size = "md",
}) => {
  const [imgError, setImgError] = useState(false);
  const isUS = item.port_api_type !== "th_stock";
  const logoUrl = isUS
    ? `https://assets.parqet.com/logos/symbol/${item.symbol}?format=svg`
    : null;

  const dim = size === "sm" ? "w-8 h-8" : "w-10 h-10";
  const radius = size === "sm" ? "rounded-[6px]" : "rounded-[6px]";
  const text = size === "sm" ? "text-[11px]" : "text-xs";

  const avatarClass = `${dim} ${radius} flex items-center justify-center text-white ${text} font-bold flex-shrink-0 ${
    item.port_api_type === "th_stock"
      ? "bg-gradient-to-br from-blue-500 to-purple-600"
      : "bg-gradient-to-br from-gray-600 to-gray-800"
  }`;

  if (logoUrl && !imgError) {
    return (
      <div
        className={`${dim} ${radius} flex-shrink-0 bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-600 overflow-hidden flex items-center justify-center`}
      >
        <img
          src={logoUrl}
          alt={item.symbol}
          className="w-full h-full object-contain"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }
  return <div className={avatarClass}>{item.symbol.slice(0, 2)}</div>;
};

const SkeletonBar: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`bg-gray-200 dark:bg-gray-600 rounded animate-pulse ${className}`} />
);

const WatchlistSkeleton: React.FC = () => (
  <div>
    {/* Real header */}
    <div className="hidden sm:grid grid-cols-[1fr_96px_80px_80px_56px_56px_64px_80px_96px_20px] items-center gap-x-6 px-6 py-4 opacity-40 table-head">
      <span>หุ้น</span>
      <span className="text-right">ราคา</span>
      <span className="text-right">%เปลี่ยน</span>
      <span className="text-right">Mkt Cap</span>
      <span className="text-right">P/E</span>
      <span className="text-right">Div%</span>
      <span className="text-right">Vol</span>
      <span className="text-right">YTD</span>
      <span className="text-right">52W</span>
      <span />
    </div>
    {Array.from({ length: 6 }).map((_, i) => (
      <div
        key={i}
        className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_96px_80px_80px_56px_56px_64px_80px_96px_20px] gap-x-6 items-center px-6 py-3.5 border-b border-gray-100 dark:border-gray-700/40"
      >
        {/* Symbol + Name */}
        <div className="flex items-center gap-3">
          <SkeletonBar className="w-8 h-8 rounded-[6px] flex-shrink-0" />
          <div className="flex flex-col gap-1.5">
            <SkeletonBar className="h-3.5 w-16" />
            <SkeletonBar className="h-2.5 w-24 opacity-60" />
          </div>
        </div>
        {/* Mobile: price stacked */}
        <div className="flex flex-col items-end gap-1.5 sm:hidden">
          <SkeletonBar className="h-3.5 w-16" />
          <SkeletonBar className="h-4 w-12 rounded-md opacity-70" />
        </div>
        {/* Price */}
        <div className="hidden sm:flex justify-end w-24">
          <SkeletonBar className="h-3.5 w-16" />
        </div>
        {/* Change */}
        <div className="hidden sm:flex justify-end w-20">
          <SkeletonBar className="h-5 w-14 rounded-md" />
        </div>
        {/* Market Cap */}
        <div className="hidden sm:flex justify-end w-20">
          <SkeletonBar className="h-3 w-12" />
        </div>
        {/* P/E */}
        <div className="hidden sm:flex justify-end w-14">
          <SkeletonBar className="h-3 w-8" />
        </div>
        {/* Div% */}
        <div className="hidden sm:flex justify-end w-14">
          <SkeletonBar className="h-3 w-8" />
        </div>
        {/* Volume */}
        <div className="hidden sm:flex justify-end w-16">
          <SkeletonBar className="h-3 w-10" />
        </div>
        {/* YTD Sparkline */}
        <div className="hidden sm:flex justify-end w-20">
          <SkeletonBar className="h-8 w-16 rounded" />
        </div>
        {/* 52W bar */}
        <div className="hidden sm:flex justify-end w-24">
          <div className="w-full">
            <SkeletonBar className="h-2 w-full rounded-full mb-1" />
            <div className="flex justify-between">
              <SkeletonBar className="h-2 w-6" />
              <SkeletonBar className="h-2 w-6" />
            </div>
          </div>
        </div>
        <span className="hidden sm:block" />
      </div>
    ))}
  </div>
);

const Watchlist: React.FC = () => {
  const {
    items,
    prices,
    sparklines,
    loading,
    pricesLoading,
    analysisResults,
    removeItem,
    fetchPrices,
    analyzeStock,
  } = useWatchlistStore();

  const AI_ENABLED = false;

  const [showAddModal, setShowAddModal] = useState(false);
  const [analysisItem, setAnalysisItem] = useState<WatchlistItem | null>(null);
  const [chartSymbol, setChartSymbol] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const sortedItems = useMemo(() => {
    if (!sortCol) return items;
    return [...items].sort((a, b) => {
      const pa = prices[a.symbol];
      const pb = prices[b.symbol];
      let aVal: number | string | null = null;
      let bVal: number | string | null = null;
      switch (sortCol) {
        case "symbol":
          aVal = a.symbol;
          bVal = b.symbol;
          break;
        case "sector":
          aVal = pa?.sector ?? "";
          bVal = pb?.sector ?? "";
          break;
        case "price":
          aVal = pa?.price ?? null;
          bVal = pb?.price ?? null;
          break;
        case "change":
          aVal = pa?.changePercent ?? null;
          bVal = pb?.changePercent ?? null;
          break;
        case "marketCap":
          aVal = pa?.marketCap ?? null;
          bVal = pb?.marketCap ?? null;
          break;
        case "pe":
          aVal = pa?.pe ?? null;
          bVal = pb?.pe ?? null;
          break;
        case "dividend":
          aVal = pa?.dividendYield ?? null;
          bVal = pb?.dividendYield ?? null;
          break;
        case "volume":
          aVal = pa?.volume ?? null;
          bVal = pb?.volume ?? null;
          break;
      }
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === "string" && typeof bVal === "string")
        return sortDir === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      return sortDir === "asc"
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [items, prices, sortCol, sortDir]);

  const handleAnalyze = (item: WatchlistItem, force = false) => {
    setAnalysisItem(item);
    if (force || !analysisResults[item.symbol]) {
      analyzeStock(item.symbol, item, force);
    }
  };

  const handleCloseAnalysis = () => {
    setAnalysisItem(null);
  };

  const formatPrice = (item: WatchlistItem) => {
    const p = prices[item.symbol];
    if (!p) return "—";
    const symbol = p.currency === "THB" ? "฿" : "$";
    return `${symbol}${p.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatChange = (item: WatchlistItem) => {
    const p = prices[item.symbol];
    if (!p) return null;
    const pct = p.changePercent;
    const sign = pct >= 0 ? "+" : "";
    return `${sign}${pct.toFixed(2)}%`;
  };

  const formatVolume = (vol: number | null) => {
    if (!vol) return "—";
    if (vol >= 1_000_000_000) return `${(vol / 1_000_000_000).toFixed(2)}B`;
    if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(2)}M`;
    if (vol >= 1_000) return `${(vol / 1_000).toFixed(1)}K`;
    return vol.toLocaleString();
  };

  const formatMarketCap = (mc: number | null, currency: string) => {
    if (!mc) return "—";
    const sym = currency === "THB" ? "฿" : "$";
    if (mc >= 1_000_000_000_000)
      return `${sym}${(mc / 1_000_000_000_000).toFixed(2)}T`;
    if (mc >= 1_000_000_000) return `${sym}${(mc / 1_000_000_000).toFixed(2)}B`;
    if (mc >= 1_000_000) return `${sym}${(mc / 1_000_000).toFixed(2)}M`;
    return `${sym}${mc.toLocaleString()}`;
  };

  const isPositive = (item: WatchlistItem) => {
    const p = prices[item.symbol];
    return p ? p.changePercent >= 0 : null;
  };

  return (
    <>
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-2"></div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPrices}
            disabled={pricesLoading || items.length === 0}
            className={buttonClass('secondary', 'sm')}
          >
            <RefreshCw
              className={`w-auto h-4 ${pricesLoading ? "animate-spin text-primary-600" : ""}`}
            />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className={buttonClass('primary', 'sm')}
          >
            <Plus className="w-4 h-4" />
            เพิ่มหุ้น
          </button>
        </div>
      </div>

      {/* Watchlist Content */}
      {loading ? (
        <WatchlistSkeleton />
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center border-t border-gray-100 dark:border-gray-700/50">
          <div className="w-16 h-16 rounded-2xl bg-brand-soft flex items-center justify-center mb-4">
            <Bookmark className="w-8 h-8 text-primary-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1">
            ยังไม่มีหุ้นใน Watchlist
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            กดปุ่ม "เพิ่มหุ้น" เพื่อเริ่ม track หุ้นที่สนใจ
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className={buttonClass('primary', 'sm')}
          >
            <Plus className="w-4 h-4" />
            เพิ่มหุ้น
          </button>
        </div>
      ) : (
        <div>
          {/* Table header */}
          {(() => {
            const cols = [
              { key: "symbol", label: "หุ้น", width: "", align: "left" },
              { key: "price", label: "ราคา", width: "w-24", align: "right" },
              {
                key: "change",
                label: "%เปลี่ยน",
                width: "w-20",
                align: "right",
              },
              {
                key: "marketCap",
                label: "Mkt Cap",
                width: "w-20",
                align: "right",
              },
              { key: "pe", label: "P/E", width: "w-14", align: "right" },
              { key: "dividend", label: "Div%", width: "w-14", align: "right" },
              { key: "volume", label: "Vol", width: "w-16", align: "right" },
            ] as const;
            return (
              <div className="hidden sm:grid grid-cols-[1fr_96px_80px_80px_56px_56px_64px_80px_96px_20px] items-center gap-x-6 px-6 py-4 table-head">
                {cols.map(({ key, label, width, align }) => {
                  const active = sortCol === key;
                  const Icon = active
                    ? sortDir === "asc"
                      ? ArrowUp
                      : ArrowDown
                    : ArrowUpDown;
                  return (
                    <button
                      key={key}
                      onClick={() => handleSort(key)}
                      className={`flex items-center gap-1 ${align === "right" ? "justify-end" : ""} ${width} select-none transition-colors ${active ? "text-primary-600 dark:text-primary-400" : "hover:text-primary-700 dark:hover:text-primary-300"}`}
                    >
                      {label}
                      <Icon
                        className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"}`}
                      />
                    </button>
                  );
                })}
                <div className="flex justify-end w-20">YTD</div>
                <div className="flex justify-end w-24">52W</div>
                <span />
              </div>
            );
          })()}

          {/* Rows */}
          <div>
            {sortedItems.map((item) => {
              const positive = isPositive(item);
              const changeStr = formatChange(item);
              const priceStr = formatPrice(item);
              const p = prices[item.symbol];

              return (
                <div
                  key={item.id}
                  className="group/row grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_96px_80px_80px_56px_56px_64px_80px_96px_20px] gap-x-6 items-center px-6 py-3.5 border-b border-gray-100 dark:border-gray-700/40 border-l-2 border-l-transparent hover:border-l-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-all duration-150"
                >
                  {/* Symbol + Name */}
                  <button
                    className="flex items-center gap-3 min-w-0 text-left"
                    onClick={() => {
                      const tv =
                        item.port_api_type === "th_stock"
                          ? `SET:${item.symbol}`
                          : item.symbol;
                      setChartSymbol(tv);
                    }}
                    title="ดูกราฟ"
                  >
                    <StockLogo item={item} size="sm" />
                    <div className="min-w-0 flex flex-col gap-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-bold text-gray-900 dark:text-white group-hover/row:text-primary-600 dark:group-hover/row:text-primary-400 transition-colors flex-shrink-0">
                          {item.symbol}
                        </span>
                        <p className="text-xs text-gray-400 truncate leading-tight">
                          {item.name}
                        </p>
                      </div>
                      {p?.sector && (
                        <span className="text-[9px] px-1 py-px rounded bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-400 hidden sm:inline-block leading-tight w-fit">
                          {p.sector}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Mobile: price+change stacked */}
                  <div className="flex flex-col items-end gap-0.5 sm:hidden">
                    {pricesLoading && !p ? (
                      <div className="h-4 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    ) : p ? (
                      <>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {priceStr}
                        </span>
                        {changeStr && (
                          <span
                            className={`text-xs font-semibold px-1.5 py-0.5 rounded-md ${positive ? "bg-gradient-to-r from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-gain" : "bg-gradient-to-r from-rose-50 to-pink-100 dark:from-rose-900/30 dark:to-pink-900/30 text-loss"}`}
                          >
                            {changeStr}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </div>

                  {/* Price */}
                  <div className="hidden sm:block text-right w-24">
                    {pricesLoading && !p ? (
                      <div className="h-4 w-16 ml-auto bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    ) : (
                      <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                        {priceStr}
                      </span>
                    )}
                  </div>

                  {/* Change % */}
                  <div className="hidden sm:flex justify-end w-20">
                    {pricesLoading && !p ? (
                      <div className="h-5 w-14 bg-gray-200 dark:bg-gray-600 rounded-md animate-pulse" />
                    ) : p && changeStr ? (
                      <span
                        className={`inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-md tabular-nums ${positive ? "bg-gradient-to-r from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-gain" : "bg-gradient-to-r from-rose-50 to-pink-100 dark:from-rose-900/30 dark:to-pink-900/30 text-loss"}`}
                      >
                        {positive ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {changeStr}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-300 dark:text-gray-400">
                        —
                      </span>
                    )}
                  </div>

                  {/* Market Cap */}
                  <div className="hidden sm:block text-right w-20">
                    {pricesLoading && !p ? (
                      <div className="h-3 w-12 ml-auto bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    ) : (
                      <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                        {p ? (
                          formatMarketCap(p.marketCap, p.currency)
                        ) : (
                          <span className="text-gray-300 dark:text-gray-400">—</span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* P/E */}
                  <div className="hidden sm:block text-right w-14">
                    {pricesLoading && !p ? (
                      <div className="h-3 w-8 ml-auto bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    ) : (
                      <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                        {p?.pe != null ? (
                          p.pe.toFixed(2)
                        ) : (
                          <span className="text-gray-300 dark:text-gray-400">—</span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Div% */}
                  <div className="hidden sm:block text-right w-14">
                    {pricesLoading && !p ? (
                      <div className="h-3 w-8 ml-auto bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    ) : p?.dividendYield != null ? (
                      <span className="text-xs font-medium tabular-nums text-gain">
                        {p.dividendYield}%
                      </span>
                    ) : (
                      <span className="text-xs text-gray-300 dark:text-gray-400">
                        —
                      </span>
                    )}
                  </div>

                  {/* Volume */}
                  <div className="hidden sm:block text-right w-16">
                    {pricesLoading && !p ? (
                      <div className="h-3 w-10 ml-auto bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    ) : (
                      <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                        {p ? (
                          formatVolume(p.volume)
                        ) : (
                          <span className="text-gray-300 dark:text-gray-400">—</span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* YTD Sparkline */}
                  <div className="hidden sm:flex justify-end w-20">
                    {pricesLoading && !sparklines[item.symbol] ? (
                      <div className="h-8 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                    ) : sparklines[item.symbol] ? (
                      <Sparkline
                        data={sparklines[item.symbol]}
                        positive={positive !== false}
                      />
                    ) : (
                      <span className="text-xs text-gray-300 dark:text-gray-400">
                        —
                      </span>
                    )}
                  </div>

                  {/* 52W bar */}
                  <div className="hidden sm:flex justify-end w-24">
                    {pricesLoading && !p ? (
                      <div className="w-full">
                        <div className="h-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full animate-pulse mb-1" />
                        <div className="flex justify-between">
                          <div className="h-2 w-6 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                          <div className="h-2 w-6 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                        </div>
                      </div>
                    ) : p?.high52w && p?.low52w ? (
                      (() => {
                        const pct = Math.min(
                          100,
                          Math.max(
                            2,
                            ((p.price - p.low52w) / (p.high52w - p.low52w)) *
                              100,
                          ),
                        );
                        const sym = p.currency === "THB" ? "฿" : "$";
                        return (
                          <div className="w-full">
                            <div className="relative h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-visible mb-1">
                              <div
                                className={`h-full rounded-full ${positive ? "bg-gain-gradient" : "bg-loss-gradient"}`}
                                style={{ width: `${pct}%` }}
                              />
                              <div
                                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white border-2 border-gray-400 dark:border-gray-300 shadow-sm"
                                style={{ left: `calc(${pct}% - 4px)` }}
                              />
                            </div>
                            <div className="flex justify-between text-[10px] text-gray-400 tabular-nums">
                              <span>
                                {sym}
                                {p.low52w.toFixed(0)}
                              </span>
                              <span>
                                {sym}
                                {p.high52w.toFixed(0)}
                              </span>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <span className="text-xs text-gray-300 dark:text-gray-400">
                        —
                      </span>
                    )}
                  </div>

                  {/* Delete */}
                  <button
                    onClick={() => removeItem(item.id)}
                    className="hidden sm:flex p-0.5 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all opacity-0 group-hover/row:opacity-100"
                    title="ลบ"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <TradingViewModal
        isOpen={chartSymbol !== null}
        symbol={chartSymbol ?? ''}
        onClose={() => setChartSymbol(null)}
      />
      <AddToWatchlistModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />
      {AI_ENABLED && (
        <AIAnalysisModal
          item={analysisItem}
          onClose={handleCloseAnalysis}
          onRefresh={(item) => handleAnalyze(item, true)}
        />
      )}
    </>
  );
};

export default Watchlist;
